import React, { useState } from 'react';
import { Recording, Service, Tag } from '../../../types/rest/assetCatalogTypes';
import { useDispatch } from 'react-redux';
import { CallbackFn, useFormInput, ValidatorType } from '../../../hooks/useFormInput';
import { Col, Form, Row, Button } from 'react-bootstrap';
import styles from '../../MockServices/Tabs/MockServices/MockServices.module.css';
import BzFormControlWithHook from '../../../components/forms/BzFormControlWithHook';
import HelpTooltip from '../../../components/overlays/helpTooltip/HelpTooltip';
import ServicePicker from '../../../components/ui/servicePicker/ServicePicker';
import TagSelector from '../../../components/selectors/TagSelector';
import MixpanelService from '../../../utils/mixpanelService';
import i18n from '../../../i18n';
import { CHANGE_TRANSACTION_SERVICE } from '../../../utils/mixpanelEvents';
import RecordingMessages from './Messages/RecordingMessages';
import _ from 'lodash';
import { createPostRecording, updatePatchRecording } from '../../../store/recordings/recordingsActions';
import BzmIcon from '../../../components/icons/BzmIcon';
import clsx from 'clsx';

interface RecordingEditorProps {
  recording: Recording;
  isReadOnly: boolean;
  isCreate: boolean;
  isCompact?: boolean; //Used for compact view in the shuttle
}

const RecordingEditor = ({ recording, isCompact }: RecordingEditorProps) => {
  const [recordingState, setRecordingState] = useState<Recording>(recording);
  const dispatch = useDispatch();
  const saveRecording = () => {
    const patchRecording = {
      serviceId: recordingState.serviceId,
      name: recordingState.name,
      description: recordingState.description,
      tags: recordingState.tags,
    };

    if (recordingState.id === -1) {
      dispatch(createPostRecording(patchRecording, recordingState.name));
      return;
    }
    dispatch(updatePatchRecording(recordingState.id, patchRecording, recordingState.name));
  };

  const updateState = (field: string, value: any) => {
    const updatedState = _.cloneDeep(recordingState);
    _.set(updatedState, field, value);
    setRecordingState(updatedState);
  };

  const nameInput = useFormInput(
    recordingState.name,
    (fn: CallbackFn) => updateState('name', fn.value),
    { type: ValidatorType.REQUIRED, invalidMessage: i18n.t('recordings:editor.recordingNameRequired') },
    { type: ValidatorType.MAX_LENGTH, value: 250, invalidMessage: i18n.t('recordings:editor.recordingNameMaxLength') },
    { type: ValidatorType.INVALID_LEADING_SPACES, invalidMessage: i18n.t('recordings:editor.recordingNameIsInvalid') },
    { type: ValidatorType.INVALID_CHARACTERS, invalidMessage: i18n.t('recordings:editor.recordingNameIsInvalid') },
  );

  const handleServiceChange = (vs: Service | null) => {
    const updatedState = _.cloneDeep(recordingState);
    updatedState.serviceId = vs.id;
    updatedState.serviceName = vs.name;
    setRecordingState(updatedState);
    MixpanelService.track(CHANGE_TRANSACTION_SERVICE());
  };

  const initialMessagesPlaceholder = () => {
    return (
      <div className={'p-2'}>
        <div className={'d-flex justify-content-center align-items-center'}>
          <div className="text-center mt-2">
            <BzmIcon variant={'icon-bzm-info '} className={'icon-md text-muted'} />
            <div className="font-size-xlg font-weight-semi-bold">No Messages Yet</div>
            <div className="p-1 bd-highlight text-center" style={{ maxWidth: '600px' }}>
              <p>You can start adding new messages after you save the recording.</p>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className={!isCompact && 'card'}>
      <div className={clsx('card-body', isCompact && 'pt-1')}>
        {!isCompact && (
          <>
            <Row>
              <Col md={4}>
                <Form.Group controlId="input-recording-name">
                  <div className="form-group-heading">
                    <Form.Label>{i18n.t('recordings:editor.name')}</Form.Label>
                  </div>
                  <div className="position-relative">
                    <BzFormControlWithHook
                      autoComplete="off"
                      useFormInputHook={nameInput}
                      placeholder={i18n.t('recordings:editor.enterName')}
                      required
                    />
                  </div>
                </Form.Group>
              </Col>
              <Col md={7}>
                <Form.Group controlId="input-description">
                  <Form.Label>{i18n.t('recordings:editor.description')}</Form.Label>
                  <Form.Control
                    autoComplete="off"
                    as="input"
                    onChange={event => updateState('description', (event.target as HTMLInputElement).value)}
                    defaultValue={recordingState.description ?? ''}
                    placeholder={i18n.t('recordings:editor.enterDescription')}
                  />
                </Form.Group>
              </Col>
              <Col md={1} className="text-md-right">
                <Button
                  disabled={nameInput.valid === false}
                  onClick={() => saveRecording()}
                  id="btn-save-recording"
                  title={i18n.t('recordings:editor.saveRec')}
                >
                  {i18n.t('common:actions.save')}
                </Button>
              </Col>
            </Row>
            <Row>
              <Col md={4}>
                <Form.Group>
                  <div className="form-group-heading justify-content-start">
                    <Form.Label className="d-inline" htmlFor={'service-picker-' + recordingState.serviceId}>
                      Service
                    </Form.Label>
                    <span title={i18n.t('common:showHelp')} className={styles.questionNearService}>
                      <HelpTooltip id={'message-queue-help'} />
                    </span>
                  </div>
                  <ServicePicker
                    isCreateEnabled
                    id={'service-picker-' + recordingState.serviceId}
                    selectHandler={handleServiceChange}
                    isSelectAll={false}
                    preSelected={recordingState.serviceId}
                  />
                  {(!recordingState.serviceId || recordingState.serviceId === 0) && false && (
                    <div className="invalid-feedback">{i18n.t('recordings:editor.serviceRequired')}</div>
                  )}
                </Form.Group>
              </Col>
              <Col md={7}>
                <Form.Group>
                  <Form.Label htmlFor="react-select-multiple">{i18n.t('recordings:table.tags')}</Form.Label>
                  <TagSelector
                    preSelected={recordingState.tags}
                    selectHandler={(tags: Tag[]) =>
                      updateState(
                        'tags',
                        tags.map(t => t.name),
                      )
                    }
                  />
                </Form.Group>
              </Col>
              <Col md={1}></Col>
            </Row>
          </>
        )}
        {recording.id !== -1 ? (
          <RecordingMessages recording={recordingState} isCompact={isCompact} />
        ) : (
          initialMessagesPlaceholder()
        )}
      </div>
    </div>
  );
};

export default RecordingEditor;
